import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import { disconnectAppsAndSettingsUrl } from './fbUrls'
import { FacebookSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { EXTENSION_MESSAGE_TYPES } from '../../utils/enums'
import * as enums from '../../utils/enums'
import { logError } from '../../utils/loggingUtils'
import { getMessageType } from '../../utils/messages'
import { StageDef, UserContext } from '../automationMachineInterfaces'
import * as promises from '../interruptiblePromises'
import {
  evaluateXpath,
  getCaseInsensitiveXPathTextSelector,
} from '../interruptibleXpath'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

// Settings property functions
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => (value ? value.join(', ') : null))
const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) =>
    value ? Object.keys(value).length === 0 : false,
  )
const getCantUndoFn = () => defaultCantUndoFn
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

// DOM Search Methods

function findRemoveButtons(contextNode: Node): HTMLElement[] {
  const candidates = evaluateXpath(
    contextNode,
    "//*[text()='Remove' and count(./ancestor::*[@aria-disabled='true' or @aria-hidden='true']) = 0]",
  )

  return candidates as HTMLElement[]
}

function findCancelButtons(contextNode: Node): HTMLElement[] {
  const candidates = evaluateXpath(
    contextNode,
    "//*[@aria-label='Cancel' and count(./ancestor::*[@aria-disabled='true' or @aria-hidden='true']) = 0]",
  )

  return candidates as HTMLElement[]
}

function findAppIcons(contextNode: Node): HTMLImageElement[] {
  const candidates = evaluateXpath(
    contextNode,
    '//img[contains(@alt, "application logo image")]',
  )

  return candidates as HTMLImageElement[]
}

function findLikelyAppNameGivenIcon(iconNode: Node): string {
  const appNameSpan = evaluateXpath(
    iconNode,
    './following-sibling::div//span',
  )[0]

  return appNameSpan?.textContent
}

function getSpansWithinRemoveModal(): HTMLElement[] {
  return evaluateXpath(
    document,
    `//*[
            (starts-with(text(), "Remove") and contains(text(), "?")) or
            starts-with(text(), "Once removed") or
            starts-with(text(), "This will remove all your connections")
        ]`,
  ) as HTMLElement[]
}

function getRemoveModalAppearsListener(): Promise<Node> {
  const modalAppearsPromise =
    promises.getWaitForElementToAppearPromise<HTMLElement>(
      document,
      () => getSpansWithinRemoveModal()[0],
    )

  return modalAppearsPromise
}

function getRemoveModalDisappearsListener(): Promise<Node> {
  const isModalGone = () => {
    const results = getSpansWithinRemoveModal()
    return results.length === 0
  }

  return promises.getDomPromise(document, isModalGone)
}

function getRemoveModalRemoveButton(): HTMLElement {
  const contextNodes = getSpansWithinRemoveModal()
  const candidates = findRemoveButtons(contextNodes[0])
  return candidates[0]
}

function getRemoveModalCancelButton(): HTMLElement {
  const contextNodes = getSpansWithinRemoveModal()
  const candidates = findCancelButtons(contextNodes[0])
  return candidates[0]
}

function getDisconnectionConfirmedListener(): Promise<object> {
  return promises.getMessagePromise((msg) => {
    const condition =
      getMessageType(msg) ===
      EXTENSION_MESSAGE_TYPES.FB___APP_DISCONNECT_CONFIRMED
    return condition
  })
}

// Script

function showAllApps() {
  const getButtonClickAndWait = () => {
    const buttonElementPromise = promises.getWaitForElementToAppearPromise(
      document,
      (node) =>
        evaluateXpath<HTMLElement>(
          node,
          `//div[@role="button" and ${getCaseInsensitiveXPathTextSelector(
            'aria-label',
            'see more',
          )}]`,
        )[0],
    )

    return Promise.race([
      buttonElementPromise.then((button) => {
        const waitForPageToChange = promises.getDomPromise(document, () => true)
        button.click()
        return waitForPageToChange.then(getButtonClickAndWait)
      }),
      promises.wait(5000).then(() => null),
    ])
  }

  return getButtonClickAndWait()
}

function getListOfApps() {
  const appIcons = findAppIcons(document)
  const apps = appIcons.map(findLikelyAppNameGivenIcon)

  return apps
}

function gatherConnectedApps({
  updateStoredInitialValues,
}: UserContext): Promise<void> {
  // Use the buttons to locate ourselves
  const apps = getListOfApps()

  return updateStoredInitialValues(
    { [FacebookSettingKey.CONNECTED_APPS]: apps },
    enums.FBRecommendationKey.FB_CONNECTED_APPS,
  )
}

async function disconnectAllApps(isDry: boolean): Promise<void> {
  const removeButtons = evaluateXpath<HTMLElement>(
    document,
    `.//div[${getCaseInsensitiveXPathTextSelector(
      'aria-label',
      'remove',
    )} and @role="button" and not(@aria-disabled="true")]`,
  )

  if (!removeButtons) {
    // no remove buttons found, no apps to remove
    return undefined
  }

  for (const removeButton of removeButtons) {
    const modalAppearsListener = getRemoveModalAppearsListener()
    removeButton.click()

    await modalAppearsListener

    let removeModalButton
    let completedListener

    if (!isDry) {
      removeModalButton = getRemoveModalRemoveButton()
      completedListener = getDisconnectionConfirmedListener()
    } else {
      removeModalButton = getRemoveModalCancelButton()
      completedListener = getRemoveModalDisappearsListener()
    }

    if (!removeModalButton) {
      throw new Error(
        'Could not find element for Remove modal button in Facebook connected apps',
      )
    }
    removeModalButton.click()

    await completedListener
    await promises.wait(500)
  }

  return undefined
}

enum StageKey {
  GATHER_SETTINGS_CONNECTED_APPS = 'GATHER_SETTINGS_CONNECTED_APPS',
  UPDATE_REMOVE_CONNECTED_APPS = 'UPDATE_REMOVE_CONNECTED_APPS',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTINGS_CONNECTED_APPS,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: disconnectAppsAndSettingsUrl,
    urlPattern: /https:\/\/www\.facebook\.com\/settings\?tab=applications.*/,
    action: async (machineContext, userContext) => {
      await showAllApps()
      await gatherConnectedApps(userContext)
    },
  },
]

// Executor

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_REMOVE_CONNECTED_APPS,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: disconnectAppsAndSettingsUrl,
    action: async (machineContext, userContext) => {
      const { isDry } = userContext
      await showAllApps()

      try {
        await disconnectAllApps(isDry)
      } catch (error) {
        logError(error, 'fbConnectedApps.ts')
      }
    },
  },
]

const undoStageDefs: StageDef[] = []

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
